import { Container } from 'ui';
import { Head } from 'head/Head';
import { HelpItem as Item } from 'components/business/help/HelpItem';
import { Link, Typography } from 'omakase';
import { StoreType } from '@tock/shared/utils/connect';
import { consumer } from '@tock/coretypes';
import {
  consumerHelp,
  consumerHelpSection,
  consumerHelpTitle,
  paraBeforeList,
} from 'routes/business/helpRouteCssDefs';
import { getTitleSuffix } from 'utils/seo';
import { isBraintreeBusiness } from 'utils/businessUtils';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { useFeatureFlagForConsumer } from 'featureFlags/useConsumerFeatureFlag';
import { useSelector } from 'react-redux';
import Anchor from 'components/Anchor';
import React from 'react';

const HelpRoute: React.FunctionComponent = () => {
  const { config } = useSelector((state: StoreType) => ({
    config: state.app.config,
  }));

  const isLockoutEnabled = useFeatureFlagForConsumer('LOGIN_FAILURES_LOCK_ACCOUNT');

  const isTockGiftCardDeprecated = useFeatureFlag('GOODBYE_TOCK_GIFT_CARDS');

  const business = config?.business;

  const myReservationsLink = `/profile/reservations`;
  const myWaitlistRequestsLink = `${myReservationsLink}/waitlist`;
  const myAccountProfileLink = `/profile/account/profile`;
  const showGiftCardFaqs = !isTockGiftCardDeprecated
    ? business
      ? isBraintreeBusiness(business as consumer.BusinessConfig)
      : true
    : false;

  const supportFormLink = 'https://www.surveymonkey.com/r/VQGTSY5';

  return (
    <div className="Legal">
      <Container maxWidth="md">
        <Head
          title={`${business?.name ?? 'Tock'} - Help Center${getTitleSuffix(business)}`}
          description="Can I cancel my reservation? Ordinary reservations can be canceled through Tock at any time.
       Prepaid reservations or reservations requiring a deposit cannot be canceled through Tock and
       are considered final. Read the restaurant's FAQs for more details."
        />
        <Typography
          component="h1"
          css={consumerHelpTitle}
          mb={4}
          variant="heading01"
          align="center"
        >
          Tock Help Center
        </Typography>

        <div css={consumerHelp}>
          <div css={consumerHelpSection}>
            <Item title="Reservation FAQ" bigger>
              <Item title="I need help with my reservation.">
                <p>
                  While we provide the technology for our partners, we cannot take any action on
                  their behalf. If you have questions or need help with your reservation, we
                  recommend contacting the business directly. You can find their contact information
                  on your <Link href={myReservationsLink}>reservation confirmation</Link> in your
                  profile, or in your reservation confirmation email.
                </p>
              </Item>

              <Item title="Can I cancel my reservation?">
                <p>
                  Your <Link href={myReservationsLink}>reservation confirmation</Link> email will
                  contain specific information about the cancellation policy for your reservation,
                  so please review this policy for more information.
                </p>
                <p>
                  Free reservations can always be canceled through Tock, but each business on Tock
                  sets their own cancellation policies for deposit and prepaid bookings. The
                  policies set by each business on Tock will determine if, when, and how a
                  reservation can be canceled.
                </p>
                <p>
                  When online cancellation is available for your reservation, you will be able to
                  cancel your booking from the{' '}
                  <Link href={myReservationsLink}>reservation confirmation</Link> in your profile,
                  or from within your reservation confirmation email. Some businesses may request
                  that you contact them directly if you wish to cancel your reservation.
                </p>
              </Item>

              <Item title="How can I see and manage my waitlist requests?">
                <p>
                  <Link href={myWaitlistRequestsLink}>Active waitlist requests</Link> will be shown
                  alongside your reservations via your Tock app or browser. You will have the option
                  to remove yourself from the waitlist.
                </p>
              </Item>

              <Item title="Can I transfer my reservation?">
                <p>
                  Transferring allows you to give or sell your reservation for any amount up to the
                  original purchase price.
                </p>

                <p>
                  Some paid reservations and events can be transferred through Tock, but each
                  business has their own transfer policies.
                </p>

                <p>
                  Tock To Go orders, reservations with discounts, and reservations booked with Chase
                  points cannot be transferred. Only one transfer can be in progress at any time.
                </p>

                <p>
                  If transferring is available for your reservation, you will be able to transfer it
                  from the <Link href={myReservationsLink}>reservation confirmation</Link> in your
                  profile or by clicking “Manage” from within your reservation confirmation email.
                </p>
              </Item>

              <Item title="Why do I get charged when adding a credit card?">
                Adding a credit card may require a small test charge to validate your account. Don’t
                worry, this is a test charge that will disappear after confirming your account.
              </Item>

              <Item title="I have a question about my deposit">
                Please contact the business for any questions related to reservation deposits. You
                can find their contact information on your{' '}
                <Link href={myReservationsLink}>reservation confirmation</Link> in your profile, or
                in your reservation confirmation email.
              </Item>
            </Item>
          </div>

          <div css={consumerHelpSection}>
            <Item title="Profile FAQ" bigger>
              <Anchor
                style={{
                  scrollMarginTop: '150px',
                }}
                id="DELETE_ACCOUNT"
              >
                <Item title="I want to delete my Tock account.">
                  <p css={paraBeforeList}>
                    As long as you don’t have any upcoming reservations, you can delete your Tock
                    account by following the below self-service steps:
                  </p>
                  <ul>
                    <li>Log in to your Tock account</li>
                    <li>
                      From your <Link href={myAccountProfileLink}>Account</Link> page, scroll down
                      to Delete account section
                    </li>
                    <li>Click Delete My Account button</li>
                    <li>Select the reason for deletion</li>
                    <li>Click Delete My Account to permanently delete account</li>
                  </ul>

                  <p>
                    If your Tock account has upcoming reservations, you will need to cancel them in
                    order to delete your Tock account. If you are not able to login to your Tock
                    account but wish to delete it, you may also request manual deletion by emailing{' '}
                    <Link href="mailto:privacy@tockhq.com?subject=Delete Tock Account.">
                      privacy@tockhq.com
                    </Link>{' '}
                    with the subject “Delete Tock Account.” Please note that we may need to verify
                    your identity in connection with such manual requests, and such verification
                    process may require you to provide us with additional information.
                  </p>
                </Item>
              </Anchor>

              <Item title="What happens when I delete my Tock account?">
                <p css={paraBeforeList}>
                  Deleting your account terminates your relationship with Tock. Here’s what that
                  means:
                </p>
                <ul>
                  <li>
                    We'll delete your account and any personal data linked to your account from our
                    systems.
                  </li>
                  <li>
                    After your account is deleted, your account, gift card balance and any
                    reservations associated with your account may not be recoverable.
                  </li>
                </ul>

                <p css={paraBeforeList}>Please note that:</p>
                <ol>
                  <li>
                    deleting your account only deletes data which Tock controls. Restaurants or
                    other businesses with whom your account has made reservations or other bookings
                    via Tock may have their own data about you. With respect to any such
                    business-controlled data, you will need to reach out directly to each such
                    business.
                  </li>
                  <li>
                    our <Link href="/privacy">Privacy Policy</Link> includes additional information
                    related to account deletion.
                  </li>
                </ol>
              </Item>

              {isLockoutEnabled && (
                <Anchor
                  style={{
                    scrollMarginTop: '150px',
                  }}
                  id="ACCOUNT_LOCKOUT"
                >
                  <Item title="I'm locked out of my account">
                    <p>
                      Tock accounts are locked after 3 failed login attempts. If you are locked out
                      of your account, you will need to{' '}
                      <Link href="/forgot-password">reset your password</Link> in order to log in
                      again.
                    </p>

                    <p>
                      You can also login using your apple or google credentials with the email
                      address associated with your account.
                    </p>
                  </Item>
                </Anchor>
              )}
            </Item>
          </div>

          {showGiftCardFaqs ? (
            <div css={consumerHelpSection}>
              <Item title="Gift Card FAQ" bigger>
                <p>
                  Tock no longer offers Tock gift cards, but for those with a remaining gift card
                  balance, these items will answer the most common questions.
                </p>

                <Item title="Can I use a Tock gift card at the business?">
                  No. Tock gift cards can only be used when purchasing online.
                </Item>
                <Item title="Where can Tock gift cards be used?">
                  Gift cards can only be spent at US businesses. See complete{' '}
                  <Link href="/terms">terms and conditions</Link>.
                </Item>
                <Item title="Do gift cards expire?">
                  Once a gift card has been added to your Tock gift card balance, the funds do not
                  expire.
                </Item>
                <Item title="How do I check my gift card balance?">
                  You can check your gift card balance by logging into your Tock account and
                  visiting the{' '}
                  <Link href="/profile/account/gift-cards">Gift Cards page in your account</Link>.
                </Item>
                <Item title="Can I use a gift card on a reservation I already booked?">
                  No. Once a reservation has been booked you can’t change the payment method or add
                  funds from your gift card balance.
                </Item>
                <Item title="Can I transfer a reservation that was booked using a gift card?">
                  Yes. You can transfer and sell a paid reservation through Tock, which will debit
                  your gift card balance first and then any remaining credit card payment.
                </Item>
                <Item title="Can I transfer funds from my gift card balance to another account?">
                  No. Funds from a gift card balance cannot be transferred from one account to
                  another, but reservations can be transferred.
                </Item>
              </Item>
            </div>
          ) : null}

          <Typography variant="body02">
            Additional questions? Please contact us by completing{' '}
            <Link href={supportFormLink}>this form</Link>.
          </Typography>
        </div>
      </Container>
    </div>
  );
};

export default HelpRoute;
